import {
  ActivityFuel,
  ActivityUnit,
  CompanyReportCategory,
  Site,
  GasSourceTypeEnum,
  Tag,
  TimePeriod,
  TimePeriodTypeEnum,
  TransportMode,
} from '@shared/api/types';
import { capitalizeFirstLetter } from '@shared/lib/utils';
import { format, parse } from 'date-fns';
import { useMemo } from 'react';
import { SelectOption } from 'react-spreadsheet-import/types/types';
import { useCCF } from './ccf-context';
import { ScopeCategorySlug } from './slugs';

interface renderRowsProps {
  timePeriod: string;
  unit: string;
  timePeriods: TimePeriod[];
}

export const emptyReadingRow = {
  uuid: '',
  quantity: 0,
  unit: '',
  timePeriod: '',
};

export const renderRows = ({
  timePeriod,
  unit,
  timePeriods,
}: renderRowsProps) => {
  let number = 0;
  switch (timePeriod) {
    case TimePeriodTypeEnum.year:
      number = 1;
      break;
    case TimePeriodTypeEnum.month:
      number = 12;
      break;
  }

  const rows = [];
  for (let i = 0; i < number; i++) {
    rows.push({
      uuid: '1',
      quantity: null,
      unit: unit,
      timePeriod: timePeriods[i].uuid,
    });
  }
  return rows;
};

export function useCategory(slug: ScopeCategorySlug) {
  const { categories } = useCCF();
  return useMemo(
    () => categories?.find((c) => c.slug === slug) as CompanyReportCategory,
    [categories, slug]
  );
}

export const useGetPeriodOptions = (periods: TimePeriod[]) => {
  return useMemo(
    () =>
      periods?.map(({ uuid, name }) => ({
        value: uuid,
        label: name,
      })) as SelectOption[] | [],
    [periods]
  );
};

export function useUnitOptions(units: ActivityUnit[]) {
  return useMemo(
    () =>
      units?.map((unit) => ({
        value: unit.uuid,
        label: capitalizeFirstLetter(unit.name),
        shortLabel: unit.symbol,
      })) || [],
    [units]
  );
}

export function useFormattedSites(sites: Site[]): SelectOption[] {
  return useMemo(
    () =>
      sites.map((site) => ({
        label: site.name,
        value: site.uuid,
      })) || [],
    [sites]
  ) as SelectOption[];
}

export function useFormattedTags(tags: Tag[]): SelectOption[] {
  return useMemo(
    () =>
      tags.map((tag) => ({
        label: tag.name,
        value: tag.uuid,
      })) || [],
    [tags]
  ) as SelectOption[];
}

export function useFormattedTransportModes(
  transportModes: TransportMode[]
): SelectOption[] {
  return useMemo(
    () =>
      transportModes?.map((mode) => ({
        label: mode.name,
        value: mode.uuid,
      })) || [],
    [transportModes]
  ) as SelectOption[];
}

export function useGetRowTimePeriods(
  timePeriod: string,
  periodOptions: TimePeriod[]
) {
  return useMemo(
    () => periodOptions?.filter((period) => period.type === timePeriod),
    [periodOptions, timePeriod]
  );
}

export function useFormattedFuels(fuels: ActivityFuel[]): SelectOption[] {
  return useMemo(
    () =>
      fuels?.map((fuel) => ({
        label: fuel.name,
        value: fuel.uuid,
      })) || [],
    [fuels]
  ) as SelectOption[];
}

export function useFormattedSources(): SelectOption[] {
  return useMemo(
    () =>
      Object.keys(GasSourceTypeEnum).map((source) => ({
        label: capitalizeFirstLetter(
          GasSourceTypeEnum[source as keyof typeof GasSourceTypeEnum]
        ),
        value: GasSourceTypeEnum[source as keyof typeof GasSourceTypeEnum],
      })),
    []
  ) as SelectOption[];
}

export const ScopeDescription = {
  'scope 1': 'Direct emissions',
  'scope 2': 'Indirect emissions from purchased energy',
  'scope 3': 'Other indirect emissions',
};

export const PACKAGING_ITEMS_RATING: Record<
  string,
  { label: string; color: string; threshold: string }
> = {
  Low: {
    label: 'Low',
    color: 'var(--rating-a)',
    threshold: '<1.25',
  },
  Medium: {
    label: 'Medium',
    color: 'var(--rating-c)',
    threshold: '1.25 - 2.5',
  },
  High: {
    label: 'High',
    color: 'var(--rating-e)',
    threshold: '>2.5',
  },
};

/**
 * Formats a date range from a reporting year object
 * @param startDate - Start date in 'yyyy-MM-dd' format
 * @param endDate - End date in 'yyyy-MM-dd' format
 * @returns Formatted string like "1st Jan 2025 - 31st Dec 2025"
 */
export function formatDateRange(startDate: string, endDate: string): string {
  try {
    const start = parse(startDate, 'yyyy-MM-dd', new Date());
    const end = parse(endDate, 'yyyy-MM-dd', new Date());

    const startFormatted = format(start, 'do MMM yyyy');
    const endFormatted = format(end, 'do MMM yyyy');

    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    // Fallback to original format if parsing fails
    return `${startDate} - ${endDate}`;
  }
}

/**
 * Formats a reporting year object into a readable date range
 * @param reportingYear - The reporting year object
 * @returns Formatted string like "1st Jan 2025 - 31st Dec 2025"
 */
export function formatReportingYearRange(reportingYear: {
  startDate: string;
  endDate: string;
}): string {
  return formatDateRange(reportingYear.startDate, reportingYear.endDate);
}
